import React, { useEffect, useMemo, useState, useCallback } from 'react';
import './Dashboard.css';
import { useFetch } from "../hooks/useFetch";
import Header from "./common/Header";
import Sidebar from "./common/Sidebar";
import { useTranslation } from 'react-i18next';

export default function Subscriptions() {
  const { t, i18n } = useTranslation();
  const locale = (i18n.resolvedLanguage || 'es-CL');
  const CURRENCY = 'CLP';

  // Backend fetch (graceful with different shapes)
  const [ subsData ] = useFetch('/subscriptions', { defaultValue: [] });
  // Pull a larger slice to improve recurrence detection (up to ~12 months depending on density)
  const [ expensesData ] = useFetch('/transactions', { defaultValue: [], hasPaging: true, defaultLimit: 3000 });

  // Local working list (so you can add/pause without backend wiring yet)
  const [ items, setItems ] = useState([]);

  // UI state
  const [ search, setSearch ] = useState('');
  const [ sortBy, setSortBy ] = useState('name'); // 'name' | 'amount' | 'status'
  const [ sortDir, setSortDir ] = useState('asc');
  const [ showAdd, setShowAdd ] = useState(false);

  // Add form state
  const [ form, setForm ] = useState({
    name: '',
    amount: '',
    cycle: 'monthly', // 'monthly' | 'yearly' | 'weekly'
    next: '',
    payment: '', // e.g., Visa **** 1234
    category: '',
    status: 'active',
    notes: ''
  });

  // Helpers
  const parseAmount = (v) => {
    const n = Number(String(v).replace(/[^0-9.-]/g, ''));
    return isNaN(n) ? 0 : n;
  };
  const toYMD = (d) => {
    if (!d) return '';
    const date = new Date(d);
    if (isNaN(date)) return '';
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };
  const toStartOfDay = useCallback((d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()), []);
  const daysUntil = useCallback((d) => {
    if (!d) return null;
    const target = new Date(d);
    if (isNaN(target)) return null;
    const today = new Date();
    const ms = toStartOfDay(target) - toStartOfDay(today);
    return Math.round(ms / (1000*60*60*24));
  }, [toStartOfDay]);

  const fmtMoney = (n) => Number(n || 0).toLocaleString(locale, { style: 'currency', currency: CURRENCY });

  // Normalize payee/merchant names for grouping
  const norm = (s = '') => String(s)
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // strip accents
    .replace(/\b(s\.a\.|sa|s\.?p\.?a\.|spa|ltda|ltda\.)\b/g, '') // common suffixes
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const monthKey = (d) => {
    const x = new Date(d);
    if (isNaN(x)) return null;
    return `${x.getFullYear()}-${String(x.getMonth()+1).padStart(2,'0')}`;
  };

  // Heuristic: similar amount within ±30% or ≤ $1000 CLP absolute difference (whichever is larger)
  const similarAmount = (a, b) => {
    const aa = Math.abs(Number(a)||0), bb = Math.abs(Number(b)||0);
    const tol = Math.max(1000, 0.3 * Math.max(aa, bb));
    return Math.abs(aa-bb) <= tol;
  };

  // From expenses payload to simple list (robust to schema shape)
  const normalizedExpenses = React.useMemo(() => {
    // Accept multiple API shapes: { expenses: [...] }, { items: [...] }, or raw array
    const raw = Array.isArray(expensesData.transactions)
      ? expensesData.transactions
      : Array.isArray(expensesData?.items)
        ? expensesData.items
        : Array.isArray(expensesData)
          ? expensesData
          : [];

    return raw
      .map((e, i) => {
        const date = e.date || e.data?.date || e.created_at || e.timestamp || e.operation_date || e.post_date;
        const rawAmount = e.amount ?? e.data?.amount ?? e.value ?? e.total ?? 0;
        const amount = parseAmount(rawAmount);
        const description = e.description || e.data?.description || e.memo || e.note || '';
        // Match the same payee logic used in Expenses.js (so grouping is consistent)
        const payee =
          (e.location && e.location.name) ||
          e.data?.destination ||
          e.destination ||
          e.merchant ||
          e.vendor ||
          description ||
          '';
        const upload = e.upload || e.data?.upload || e.document || {};
        const uploadId = e.upload_id || upload.id || null;
        const bank = upload.bank_name || e.bank_name || '';
        const last4 = upload.last4 || upload.card_last4 || e.last4 || '';
        const cardLabel = `${bank ? bank : ''}${last4 ? `${bank ? ' ' : ''}•••• ${last4}` : ''}`.trim();
        const categoryName =
          e.data?.subcategory?.category?.name ||
          e.category ||
          e.data?.category ||
          '';

        return {
          id: e.id ?? i,
          date,
          amount,
          description,
          payee,
          uploadId,
          cardLabel,
          category: categoryName
        };
      })
      .filter(x => x.date && (x.amount || x.amount === 0));
  }, [expensesData]);

  // Build recurring candidates by merchant/month
  const detectedRecurring = React.useMemo(() => {
    if (!normalizedExpenses.length) return [];

    // Group by normalized payee key
    const groups = new Map();
    for (const e of normalizedExpenses) {
      const key = norm(e.payee);
      if (!key) continue;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(e);
    }

    const out = [];
    const now = new Date();
    const latestMonths = new Set();
    for (let k=0; k<12; k++) { // consider last 12 months
      const d = new Date(now.getFullYear(), now.getMonth()-k, 1);
      latestMonths.add(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`);
    }

    groups.forEach((list, key) => {
      // Bucket by month
      const byMonth = new Map();
      list.forEach(e => {
        const mk = monthKey(e.date);
        if (!mk) return;
        if (!byMonth.has(mk)) byMonth.set(mk, []);
        byMonth.get(mk).push(e);
      });

      // Keep only last 6 months
      const months = [...byMonth.keys()].filter(m => latestMonths.has(m)).sort();
      if (months.length < 2) return; // need at least 2 months to consider

      // Choose a representative amount per month (median)
      const monthAmount = months.map(m => {
        const arr = byMonth.get(m) || [];
        const vals = arr.map(x => Math.abs(x.amount)).sort((a,b)=>a-b);
        const mid = Math.floor(vals.length/2);
        const rep = vals.length ? (vals.length%2? vals[mid] : (vals[mid-1]+vals[mid])/2) : 0;
        return { m, val: rep, sample: arr };
      });

      // Check recurrence: appears in >=2 of last 3 months with similar amounts
      const last3 = monthAmount.slice(-3);
      const eligible = last3.filter(x => x.val > 0);
      if (eligible.length < 2) return;
      const base = eligible[eligible.length-1].val;
      const consistency = eligible.every(x => similarAmount(x.val, base));
      if (!consistency) return;

      // Compute name (original most frequent payee)
      const nameCounts = new Map();
      list.forEach(e => {
        const p = (e.payee||'').trim();
        if (!p) return;
        nameCounts.set(p, 1 + (nameCounts.get(p)||0));
      });
      const topName = [...nameCounts.entries()].sort((a,b)=>b[1]-a[1])[0]?.[0] || '—';

      // Compute most frequent category
      const catCounts = new Map();
      list.forEach(e => {
        const c = (e.category || '').trim();
        if (!c) return;
        catCounts.set(c, 1 + (catCounts.get(c) || 0));
      });
      const topCategory = [...catCounts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] || '';

      // Average monthly amount from last 3 months
      const last3Filtered = monthAmount.slice(-3).filter(x=>x.val>0);
      const avgMonthly = last3Filtered.length ? last3Filtered.reduce((s,x)=>s+x.val,0)/last3Filtered.length : base;

      // Next date guess: take last transaction date + ~30 days, clamp to next month
      const lastSample = last3[last3.length-1].sample;
      const lastDate = lastSample && lastSample.length ? new Date(lastSample.sort((a,b)=>new Date(b.date)-new Date(a.date))[0].date) : null;
      let nextYMD = '';
      if (lastDate && !isNaN(lastDate)) {
        const nd = new Date(lastDate.getFullYear(), lastDate.getMonth()+1, lastDate.getDate());
        nextYMD = toYMD(nd);
      }

      // Pick a payment label if available in latest sample
      const pay = (lastSample && lastSample[0]?.cardLabel) || '';

      out.push({
        id: `det-${key}`,
        name: topName,
        amount: Math.round(avgMonthly),
        cycle: 'monthly',
        next: nextYMD,
        payment: pay,
        category: topCategory,
        status: 'active',
        notes: 'Detected from expenses'
      });
    });

    // Sort by monthly amount desc
    return out.sort((a,b)=> (b.amount||0)-(a.amount||0));
  }, [normalizedExpenses]);

  useEffect(() => {
    if (!detectedRecurring || detectedRecurring.length) return;
    try {
      // Print a quick sample of normalized expenses and top merchants
      const sample = (normalizedExpenses || []).slice(0, 5);
      const counts = {};
      (normalizedExpenses || []).forEach(e => {
        const k = norm(e.payee);
        if (!k) return;
        counts[k] = 1 + (counts[k] || 0);
      });
      const top = Object.entries(counts).sort((a,b)=>b[1]-a[1]).slice(0,10);
      // eslint-disable-next-line no-console
      console.log('[Subscriptions] No detected recurring. Sample expenses:', sample);
      // eslint-disable-next-line no-console
      console.log('[Subscriptions] Top merchants (normalized):', top);
    } catch {}
  }, [detectedRecurring, normalizedExpenses]);

  // Merge backend subscriptions with detected recurring expenses
  useEffect(() => {
    const backend = Array.isArray(subsData?.subscriptions)
      ? subsData.subscriptions
      : Array.isArray(subsData) ? subsData : [];

    const fromBackend = backend.map((x, idx) => {
      const cycle = x.cycle || x.billing_cycle || 'monthly';
      const amount = parseAmount(x.amount ?? x.price ?? 0);
      const next = x.next || x.next_bill || x.next_billing_date || x.renewal || '';
      return {
        id: x.id ?? x.subscription_id ?? `srv-${idx}`,
        name: x.name || x.service || x.provider || '',
        amount,
        cycle,
        next,
        payment: x.payment || x.card || x.card_last4 ? `${x.card || '****'} ${x.card_last4 || ''}`.trim() : (x.payment_method || ''),
        category: x.category || x.label || '',
        status: x.status || 'active',
        notes: x.notes || ''
      };
    });

    // Deduplicate by normalized name (prefer backend record)
    const seen = new Set(fromBackend.map(x => norm(x.name)));
    const merged = [
      ...fromBackend,
      ...detectedRecurring.filter(d => !seen.has(norm(d.name)))
    ];

    setItems(merged);
  }, [subsData, detectedRecurring]);

  // Derived: monthly equivalence & filtered/sorted list
  const withDerived = useMemo(() => {
    const convMonthly = (amount, cycle) => {
      if (!amount) return 0;
      switch (cycle) {
        case 'yearly': return amount / 12;
        case 'weekly': return amount * (52/12);
        default: return amount; // monthly
      }
    };
    return (items || []).map(it => ({
      ...it,
      monthly: convMonthly(it.amount, it.cycle),
      daysLeft: daysUntil(it.next)
    }));
  }, [items, daysUntil]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const arr = q
      ? withDerived.filter(it => (
          (it.name || '').toLowerCase().includes(q) ||
          (it.category || '').toLowerCase().includes(q) ||
          (it.payment || '').toLowerCase().includes(q)
        ))
      : withDerived;

    const sorter = {
      name: (a,b) => (a.name||'').localeCompare(b.name||''),
      amount: (a,b) => (a.amount||0) - (b.amount||0),
      monthly: (a,b) => (a.monthly||0) - (b.monthly||0),
      next: (a,b) => new Date(a.next||0) - new Date(b.next||0),
      status: (a,b) => (a.status||'').localeCompare(b.status||'')
    }[sortBy] || (()=>0);

    const out = [...arr].sort(sorter);
    if (sortDir === 'desc') out.reverse();
    return out;
  }, [withDerived, search, sortBy, sortDir]);

  // Actions
  const addLocal = () => {
    const amt = parseAmount(form.amount);
    const next = form.next ? toYMD(form.next) : '';
    const rec = {
      id: `tmp-${Date.now()}`,
      name: form.name.trim(),
      amount: amt,
      cycle: form.cycle,
      next,
      payment: form.payment.trim(),
      category: form.category.trim(),
      status: form.status,
      notes: form.notes.trim()
    };
    setItems(prev => [rec, ...prev]);
    setShowAdd(false);
    setForm({ name:'', amount:'', cycle:'monthly', next:'', payment:'', category:'', status:'active', notes:'' });
  };
  const toggleStatus = (id) => setItems(prev => prev.map(it => it.id === id ? { ...it, status: it.status === 'active' ? 'paused' : 'active' } : it));
  const removeLocal = (id) => setItems(prev => prev.filter(it => it.id !== id));

  const headerClick = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  };

  return (
    <div className="dashboard-container">
      <Header title={t('subscriptions.title', 'Recurring expenses')} />
      <div className="dashboard-four-columns">
        <Sidebar />

        <div className="dashboard-right-column" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

          {/* Debug summary (remove later if desired) */}
          <div style={{ fontSize: 12, color: '#64748b', alignSelf: 'flex-start' }}>
            {t('subscriptions.debugCounts', 'Detected')}:{' '}
            {Array.isArray(items) ? items.filter(x => String(x.id).startsWith('det-')).length : 0}
            {' '}· {t('subscriptions.total', 'total')}: {Array.isArray(items) ? items.length : 0}
          </div>

          {/* Top controls */}
          <div className="dashboard-filters-row inside-bar" style={{ alignSelf: 'stretch' }}>
            <div className="search-input-container embedded">
              <input
                type="text"
                placeholder={t('subscriptions.search', 'Search subscriptions...')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="expense-search-input"
              />
              {search && (
                <button
                  type="button"
                  className="clear-search-button"
                  onClick={() => setSearch('')}
                  title={t('common.clear', 'Clear')}
                >
                  ×
                </button>
              )}
            </div>

            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              <button className="pretty-toggle" onClick={() => setShowAdd(s => !s)}>
                {showAdd ? t('subscriptions.hideForm', 'Hide form') : t('subscriptions.addNew', 'Add subscription')}
              </button>
            </div>
          </div>

          {/* Add form */}
          {showAdd && (
            <div className="dashboard-section" style={{ paddingTop: 12 }}>
              <h2 style={{ marginTop: 0 }}>{t('subscriptions.new', 'New subscription')}</h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(120px, 1fr))', gap: 8 }}>
                <input placeholder={t('common.name', 'Name')} value={form.name} onChange={e=>setForm({...form, name:e.target.value})} />
                <input placeholder={t('common.amount', 'Amount')} value={form.amount} onChange={e=>setForm({...form, amount:e.target.value})} />
                <select value={form.cycle} onChange={e=>setForm({...form, cycle:e.target.value})}>
                  <option value="monthly">{t('subscriptions.monthly', 'Monthly')}</option>
                  <option value="yearly">{t('subscriptions.yearly', 'Yearly')}</option>
                  <option value="weekly">{t('subscriptions.weekly', 'Weekly')}</option>
                </select>
                <input type="date" value={form.next} onChange={e=>setForm({...form, next:e.target.value})} />
                <input placeholder={t('subscriptions.payment', 'Payment method')} value={form.payment} onChange={e=>setForm({...form, payment:e.target.value})} />
                <input placeholder={t('common.category', 'Category')} value={form.category} onChange={e=>setForm({...form, category:e.target.value})} />
                <textarea placeholder={t('common.notes', 'Notes')} value={form.notes} onChange={e=>setForm({...form, notes:e.target.value})} style={{ gridColumn: '1 / -1', minHeight: 60 }} />
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button onClick={addLocal}>{t('common.add', 'Add')}</button>
                <button onClick={()=>setShowAdd(false)}>{t('common.cancel', 'Cancel')}</button>
                <span style={{ marginLeft: 'auto', opacity: 0.7 }}>{t('subscriptions.localOnly', 'Saved locally for now')}</span>
              </div>
            </div>
          )}

          {/* Summary cards */}
          <div className="dashboard-two-columns" style={{ alignSelf: 'stretch' }}>
            <div className="dashboard-section">
              <h2 style={{ marginTop: 0 }}>{t('subscriptions.monthlyTotal', 'Monthly total')}</h2>
              <div style={{ fontSize: 22, fontWeight: 700 }}>
                {fmtMoney(filtered.reduce((s, x) => s + (x.monthly || 0), 0))}
              </div>
              <div style={{ opacity: 0.7, marginTop: 6 }}>{t('subscriptions.visibleOnly', 'of visible results')}</div>
            </div>
            <div className="dashboard-section">
              <h2 style={{ marginTop: 0 }}>{t('subscriptions.count', 'Active subscriptions')}</h2>
              <div style={{ fontSize: 22, fontWeight: 700 }}>
                {filtered.filter(x => x.status === 'active').length}
              </div>
              <div style={{ opacity: 0.7, marginTop: 6 }}>{t('subscriptions.total', 'total:')} {filtered.length}</div>
            </div>
          </div>

          {/* Table */}
          <div className="dashboard-section" style={{ alignSelf: 'stretch' }}>
            <h2 style={{ marginTop: 0 }}>{t('subscriptions.list', 'Subscriptions')}</h2>
            <div style={{ maxHeight: 560, overflowY: 'auto' }}>
              <table className="dashboard-expenses-table">
                <thead>
                  <tr>
                    <th onClick={()=>headerClick('name')}>{t('common.name', 'Name')} {sortBy==='name' ? (sortDir==='asc'?'▲':'▼') : ''}</th>
                    <th onClick={()=>headerClick('amount')} className="amount-column" style={{ textAlign: 'center', minWidth: 140 }}>
                      {t('common.amount', 'Monto')} {sortBy==='amount' ? (sortDir==='asc'?'▲':'▼') : ''}
                    </th>
                    <th>{t('subscriptions.payment', 'Payment')}</th>
                    <th>{t('common.category', 'Category')}</th>
                    <th onClick={()=>headerClick('status')}>{t('common.status', 'Status')} {sortBy==='status' ? (sortDir==='asc'?'▲':'▼') : ''}</th>
                    <th>{t('common.actions', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan={6} style={{ opacity: 0.7 }}>{t('subscriptions.empty', 'No subscriptions yet')}</td></tr>
                  ) : filtered.map((s) => (
                    <tr key={s.id}>
                      <td style={{ whiteSpace: 'nowrap', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {s.name || '—'}
                        {String(s.id).startsWith('det-') && (
                          <span style={{ marginLeft: 6, fontSize: 11, padding: '1px 6px', borderRadius: 999, background: '#e2e8f0', color: '#475569' }}>
                            {t('subscriptions.auto', 'auto')}
                          </span>
                        )}
                      </td>
                      <td className="amount-column" style={{ textAlign: 'center', minWidth: 140 }}>{fmtMoney(s.amount)} <span style={{ opacity: 0.7, fontSize: 12 }}>/
                        {s.cycle === 'yearly' ? t('subscriptions.year', 'yr') : s.cycle === 'weekly' ? t('subscriptions.week', 'wk') : t('subscriptions.month', 'mo')}
                      </span></td>
                      <td>{s.payment || '—'}</td>
                      <td>{s.category || '—'}</td>
                      <td>
                        <span className={`status-badge ${s.status}`}>{s.status}</span>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button onClick={() => toggleStatus(s.id)} className="small">
                          {s.status === 'active' ? t('subscriptions.pause', 'Pause') : t('subscriptions.resume', 'Resume')}
                        </button>
                        <button onClick={() => removeLocal(s.id)} className="small danger" style={{ marginLeft: 6 }}>
                          {t('common.delete', 'Delete')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}